import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useWalkthroughStore, type WalkthroughStep } from '../store/walkthroughStore';

const API = import.meta.env.VITE_API_URL || '/api/v1';

/** Topico de ajuda para uma rota */
export interface HelpTopic {
  id: string;
  titulo: string;
  descricao: string;
  video?: string;
  legenda?: string;
  feature: string;
}

/** Tooltip de ajuda para um elemento */
export interface HelpTooltipData {
  [elementKey: string]: string;
}

/** Entrada de ajuda para uma rota */
export interface HelpRouteEntry {
  titulo: string;
  perfis: string[];
  categorias: HelpTopic[];
  tooltips: HelpTooltipData;
}

/** Manifesto completo */
export interface HelpManifest {
  version: string;
  generatedAt: string;
  routes: Record<string, HelpRouteEntry>;
}

// Cache global do manifesto (carrega uma vez)
let manifestCache: HelpManifest | null = null;
let manifestLoading = false;
let manifestPromise: Promise<HelpManifest | null> | null = null;

async function loadManifest(): Promise<HelpManifest | null> {
  if (manifestCache) return manifestCache;
  if (manifestPromise) return manifestPromise;

  manifestLoading = true;
  manifestPromise = fetch('/help_manifest.json')
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json() as Promise<HelpManifest>;
    })
    .then((data) => {
      manifestCache = data;
      manifestLoading = false;
      return data;
    })
    .catch(() => {
      manifestLoading = false;
      return null;
    });

  return manifestPromise;
}

/**
 * Hook central do sistema de ajuda contextual.
 *
 * Carrega o help_manifest.json, filtra por rota atual e perfil do usuario,
 * e expoe funcoes para abrir o dialog de ajuda.
 */
export function useHelp() {
  const location = useLocation();
  const { perfil } = useAuthStore();
  const [manifest, setManifest] = useState<HelpManifest | null>(manifestCache);
  const [isLoading, setIsLoading] = useState(!manifestCache);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const [walkthroughSteps, setWalkthroughSteps] = useState<WalkthroughStep[]>([]);
  const [catalogTopics, setCatalogTopics] = useState<HelpTopic[]>([]);
  const startWalkthrough = useWalkthroughStore(state => state.startWalkthrough);

  useEffect(() => {
    if (manifestCache) {
      setManifest(manifestCache);
      setIsLoading(false);
      return;
    }
    loadManifest().then((m) => {
      setManifest(m);
      setIsLoading(false);
    });
  }, []);

  // Buscar aulas gravadas do catálogo que combinam com a rota atual
  useEffect(() => {
    let active = true;
    const fetchCatalogHelp = async () => {
      try {
        const res = await fetch('/api/v1/ecad/catalogo');
        if (res.ok && active) {
          const data = await res.json();
          const catalogList = data.catalog || (Array.isArray(data) ? data : []);
          
          const matchingAulas: HelpTopic[] = [];
          const currentPath = location.pathname;

          for (const perfilItem of catalogList) {
            const topicos = perfilItem.topicos || [];
            for (const topicoItem of topicos) {
              const subtopicos = topicoItem.subtopicos || [];
              for (const sub of subtopicos) {
                if (!sub.videoUrl) continue;

                let routeVisited = false;
                
                if (sub.config?.urlAtual === currentPath) {
                  routeVisited = true;
                }

                if (!routeVisited && sub.roteiroEventos) {
                  try {
                    const eventos = typeof sub.roteiroEventos === 'string' 
                      ? JSON.parse(sub.roteiroEventos) 
                      : sub.roteiroEventos;

                    if (Array.isArray(eventos)) {
                      routeVisited = eventos.some(ev => 
                        ev.type === 'route' && ev.detail && ev.detail.split('?')[0] === currentPath
                      );
                    }
                  } catch (e) {
                    console.warn('Erro ao decodificar roteiroEventos:', e);
                  }
                }

                if (routeVisited) {
                  matchingAulas.push({
                    id: sub.id,
                    titulo: `Aula: ${sub.titulo}`,
                    descricao: sub.descricao || 'Vídeo explicativo gravado pelo administrador no catálogo.',
                    video: sub.videoUrl,
                    legenda: sub.legendaUrl || undefined,
                    feature: topicoItem.titulo || perfilItem.nome || 'Catálogo'
                  });
                }
              }
            }
          }
          if (active) {
            setCatalogTopics(matchingAulas);
          }
        }
      } catch (err) {
        console.warn('[useHelp] Erro ao buscar catálogo dinâmico:', err);
      }
    };
    fetchCatalogHelp();
    return () => {
      active = false;
    };
  }, [location.pathname, perfil]);

  // Buscar passos de walkthrough correspondentes no backend
  useEffect(() => {
    let active = true;
    const fetchWalkthrough = async () => {
      try {
        const url = encodeURIComponent(location.pathname);
        const perfilParam = perfil ? `&perfilId=${perfil}` : '';
        const res = await fetch(`${API}/ecad/walkthrough?url=${url}${perfilParam}`);
        if (res.ok && active) {
          const data = await res.json();
          if (data.ok && Array.isArray(data.walkthrough)) {
            setWalkthroughSteps(data.walkthrough);
          } else {
            setWalkthroughSteps([]);
          }
        }
      } catch (err) {
        console.warn('[useHelp] Erro ao buscar walkthrough:', err);
        if (active) setWalkthroughSteps([]);
      }
    };
    fetchWalkthrough();
    return () => {
      active = false;
    };
  }, [location.pathname, perfil]);

// Descricoes sintéticas geradas automaticamente por scraper de componentes de DOM
const GENERIC_DESCRIPTIONS = new Set([
  'Cards com metricas-chave resumidas no topo da pagina.',
  'Use os filtros para refinar os dados exibidos.',
  'Clique para expandir ou recolher secoes de conteudo.',
  'Copie informacoes para a area de transferencia com um clique.',
  'Use o campo de busca para localizar registros rapidamente.',
  'Exporte os dados da tabela em formato CSV, Excel ou PDF.',
  'Envie arquivos clicando no botao de upload ou arrastando.',
  'Grafico que mostra evolucao ao longo do tempo. Passe o mouse para ver valores.',
  'Distribuicao percentual dos dados. Passe o mouse para ver cada fatia.',
  'Clique em uma data para ver detalhes. Use as setas para navegar entre meses.',
  'Os badges coloridos indicam o status de cada registro (ativo, inativo, pendente).',
  'Grafico que mostra tendencias. Passe o mouse para ver valores.',
  'Tabela com informacoes organizadas em linhas e colunas.',
  'Tabela com ordenacao, busca e paginacao. Clique nos cabecalhos para ordenar.',
  'Navegue entre as paginas de resultados usando os botoes de paginacao.',
  'Alterne entre as abas para ver diferentes categorias de informacao.',
  'Mapa interativo. Use scroll para zoom e arraste para navegar.',
  'Preencha cada etapa e use "Proximo" e "Voltar" para navegar.',
  'Grafico comparativo entre categorias. Passe o mouse para ver valores.',
  'Indicador visual de progresso ou percentual.',
  'Imprima o conteudo da pagina ou gere um PDF.'
]);

function isGenericPlaceholderTopic(t: HelpTopic): boolean {
  // Aulas reais do catálogo com URL remota ou endpoint ecad são sempre válidas
  if (t.video && (t.video.startsWith('http://') || t.video.startsWith('https://') || t.video.startsWith('/ecad/'))) {
    return false;
  }
  const cleanDesc = (t.descricao || '').trim();
  return GENERIC_DESCRIPTIONS.has(cleanDesc);
}

  // Encontrar entrada de ajuda para a rota atual
  const routeEntry = useMemo(() => {
    if (!manifest) return null;

    const pathname = location.pathname;
    // Busca exata primeiro
    if (manifest.routes[pathname]) return manifest.routes[pathname];

    // Busca por prefixo estrito (evitando cair no papel base como /admin ou /master)
    const segments = pathname.split('/').filter(Boolean);
    while (segments.length > 1) {
      segments.pop();
      const prefix = '/' + segments.join('/');
      if (['/admin', '/master', '/municipal', '/estadual', '/rede', '/professor', '/equipe', '/responsavel', '/aluno'].includes(prefix)) {
        break;
      }
      if (manifest.routes[prefix]) return manifest.routes[prefix];
    }

    return null;
  }, [manifest, location.pathname]);

  // Filtrar por perfil do usuario e mesclar com tópicos dinâmicos do catálogo
  const topics = useMemo(() => {
    let baseTopics: HelpTopic[] = [];
    if (routeEntry) {
      if (!perfil || routeEntry.perfis.length === 0 || routeEntry.perfis.includes(perfil)) {
        baseTopics = routeEntry.categorias || [];
      }
    }
    // Descartar placeholders mecânicos/sintéticos sem conteúdo de documentação real
    baseTopics = baseTopics.filter((t) => !isGenericPlaceholderTopic(t));

    const merged = [...catalogTopics, ...baseTopics];
    const seen = new Set();
    return merged.filter((item) => {
      const key = `${item.id}-${item.titulo}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [routeEntry, perfil, catalogTopics]);

  const tooltips = useMemo(() => {
    if (!routeEntry) return {};
    return routeEntry.tooltips || {};
  }, [routeEntry]);

  const hasHelp = topics.length > 0 || walkthroughSteps.length > 0;
  const pageTitle = useMemo(() => {
    if (!routeEntry) return '';
    // Apenas se a rota atual for correspondência exata ou de EAD
    if (manifest && manifest.routes[location.pathname]) {
      return manifest.routes[location.pathname].titulo || '';
    }
    if (location.pathname.endsWith('/ead')) {
      return routeEntry.titulo || 'Centro de Conhecimento EAD';
    }
    return '';
  }, [routeEntry, manifest, location.pathname]);

  const openHelp = useCallback(() => {
    setSelectedTopicId(topics[0]?.id || null);
    setDialogOpen(true);
  }, [topics]);

  const openTopic = useCallback((topicId: string) => {
    setSelectedTopicId(topicId);
    setDialogOpen(true);
  }, []);

  const closeHelp = useCallback(() => {
    setDialogOpen(false);
    setSelectedTopicId(null);
  }, []);

  const selectTopic = useCallback((topicId: string) => {
    setSelectedTopicId(topicId);
  }, []);

  // Track analytics
  const trackView = useCallback((topicId: string) => {
    const topic = topics.find((t) => t.id === topicId);
    const token = localStorage.getItem('ac_token') || '';
    fetch(`${API}/help/analytics`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        rota: location.pathname,
        topico: topicId,
        titulo: topic?.titulo || null,
        feature: topic?.feature || null,
      }),
    }).catch(() => {}); // fire-and-forget
  }, [location.pathname, topics]);

  const hasWalkthrough = walkthroughSteps.length > 0;
  const startWalkthroughTour = useCallback(() => {
    setDialogOpen(false);
    startWalkthrough(walkthroughSteps);
  }, [walkthroughSteps, startWalkthrough]);

  return {
    isLoading,
    hasHelp,
    pageTitle,
    topics,
    tooltips,
    dialogOpen,
    selectedTopicId,
    openHelp,
    openTopic,
    closeHelp,
    selectTopic,
    trackView,
    walkthroughSteps,
    hasWalkthrough,
    startWalkthroughTour,
  };
}
